import { useState, useEffect, useRef } from "react";

type GalleryItem =
  | {
      type: "video";
      src: string;
      poster: string;
    }
  | {
      type: "image";
      src: string;
      alt: string;
    };

export default function StudioContentSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const touchStartX = useRef<number | null>(null);

  const galleryItems: GalleryItem[] = [
    {
      type: "image",
      src: "img/exterior.jpg",
      alt: "Yoga poses",
    },
    {
      type: "video",
      src: "https://res.cloudinary.com/dg758eb9e/video/upload/v1779756434/video1_paqwrn.mov",
      poster:
        "https://res.cloudinary.com/dg758eb9e/image/upload/v1779762629/ef622b0a-9d2c-41d6-aa0d-ca6ffa0a2db1.png",
    },
    {
      type: "video",
      src: "https://res.cloudinary.com/dg758eb9e/video/upload/v1779756817/Sound_Bath_Video_jcl0i8.mov",
      poster:
        "https://res.cloudinary.com/dg758eb9e/image/upload/v1779762685/180dbc33-0e60-4e1c-b236-45e0cf3ff397.png",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779756920/DSC00177_b794rg.jpg",
      alt: "Yoga poses",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779756893/DSC00127_pl9xly.jpg",
      alt: "Yoga stretch",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779761704/DSC00147_obkjzm.jpg",
      alt: "Yoga team",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779761730/DSC00070_vhkiad.jpg",
      alt: "Yoga team",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779756966/DSC00184_nfqrdm.jpg",
      alt: "Backbend pose",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757001/DSC00314_tsajnr.jpg",
      alt: "Yoga downward pose",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757121/IMG_2030_resized_avjwr5.jpg",
      alt: "Yoga flow",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757169/IMG_2067_resized_1_v7u37k.jpg",
      alt: "Relaxation session",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757212/IMG_2076_resized_tke71a.jpg",
      alt: "Relaxation session",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779760787/DSC00342_resized_rldjlz.jpg",
      alt: "Relaxation session",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757098/DSC00366_resized_rwqjf5.jpg",
      alt: "Stretching class",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779756985/DSC00249_m8xvko.jpg",
      alt: "Group yoga",
    },
    {
      type: "image",
      src: "https://res.cloudinary.com/dg758eb9e/image/upload/v1779757141/IMG_2043_resized_ecjszl.jpg",
      alt: "Candlelight yoga",
    },
  ];

  const total = galleryItems.length;

  const openAt = (index: number) => setActiveIndex(index);

  const close = () => setActiveIndex(null);

  const goNext = () => {
    setActiveIndex((prev) =>
      prev === null ? null : (prev + 1) % total
    );
  };

  const goPrev = () => {
    setActiveIndex((prev) =>
      prev === null ? null : (prev - 1 + total) % total
    );
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeIndex === null) return;

      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;

    const diff = e.changedTouches[0].clientX - touchStartX.current;

    if (diff > 50) goPrev();
    if (diff < -50) goNext();

    touchStartX.current = null;
  };

  const activeItem =
    activeIndex !== null ? galleryItems[activeIndex] : null;

  return (
    <section className="py-20 bg-[#f5f5f5]">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-[#00889D] mb-12">
          Our Gallery
        </h2>

        {/* GRID (UNCHANGED) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {galleryItems.map((item, index) => (
            <div
              key={index}
              onClick={() => openAt(index)}
              className="relative rounded-[22px] overflow-hidden bg-white shadow-md group cursor-pointer"
            >
              {item.type === "video" ? (
                <video
                  src={item.src}
                  poster={item.poster}
                  muted
                  playsInline
                  className="w-full h-[320px] object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-[320px] object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* LIGHTBOX */}
      {activeItem && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={close}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* CLOSE */}
          <button
            className="absolute top-6 right-6 text-white text-4xl font-bold"
            onClick={close}
          >
            ×
          </button>

          {/* LEFT ARROW */}
          <button
            className="absolute left-6 text-white text-5xl select-none"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            ‹
          </button>

          {/* RIGHT ARROW */}
          <button
            className="absolute right-6 text-white text-5xl select-none"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            ›
          </button>

          {/* MEDIA */}
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {activeItem.type === "video" ? (
              <video
                src={activeItem.src}
                poster={activeItem.poster}
                controls
                autoPlay
                className="w-full max-h-[85vh] rounded-xl"
              />
            ) : (
              <img
                src={activeItem.src}
                alt={activeItem.alt}
                className="w-full max-h-[85vh] object-contain rounded-xl"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}